import * as i7 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, EventEmitter, ElementRef, Component, Inject, ViewChild, Output, Input, NgModule } from '@angular/core';
import * as i2 from '@angular/router';
import { RouterModule } from '@angular/router';
import * as i3 from '@upr-web-primer/locale';
import { LocaleModule } from '@upr-web-primer/locale';
import * as i6 from '@upr-web-primer/minisite';
import * as i4 from '@upr-web-primer/modal';
import * as i1 from '@upr-web-primer/page-labels';
import { PageLabelsModule } from '@upr-web-primer/page-labels';
import { WINDOW } from '@upr-web-primer/window';
import { isEmpty, get, compact } from 'lodash-es';

var AnchorTarget;
(function (AnchorTarget) {
    AnchorTarget["self"] = "_self";
    AnchorTarget["blank"] = "_blank";
    AnchorTarget["newWindow"] = "New Window";
    AnchorTarget["sameWindow"] = "Same Window";
    AnchorTarget["modal"] = "Modal";
    AnchorTarget["cookieSettingModal"] = "Modal - Cookie Settings";
})(AnchorTarget || (AnchorTarget = {}));

class TcmAnchorService {
    constructor(pageLabelsService) {
        this.pageLabelsService = pageLabelsService;
        this.getOrSetPageLabels();
    }
    getOrSetPageLabels() {
        if (!isEmpty(this.pageLabels)) {
            return Promise.resolve(this.pageLabels);
        }
        return this.pageLabelsService.get().then((pageLabels) => {
            this.pageLabels = pageLabels;
            return this.pageLabels;
        });
    }
    getTarget(value) {
        return (get(value, 'Target.Values.0') ||
            get(value, 'Component.LinkedComponentValues.0.Fields.CTA.EmbeddedValues.0.CTA.EmbeddedValues.0.Target.Values.0') ||
            get(value, 'EmbeddedValues[0].Target.Values[0]') ||
            '');
    }
    getTitle(value) {
        return (get(value, 'InternalLink.LinkedComponentValues.0.Fields.Overview.EmbeddedValues.0.Heading.Values.0') ||
            get(value, 'Title.Values.0') ||
            get(value, 'Component.LinkedComponentValues.0.Fields.Overview.EmbeddedValues.0.Heading.Values.0') ||
            get(value, 'Component.LinkedComponentValues.0.MetadataFields.Navigation.EmbeddedValues.0.NavigationTitle.Values.0') ||
            get(value, 'InternalLink.LinkedComponentValues.0.MetadataFields.Navigation.EmbeddedValues.0.NavigationTitle.Values.0') ||
            get(value, 'Component.LinkedComponentValues.0.Fields.PageTitle.Values.0') ||
            get(value, 'InternalLink.LinkedComponentValues.0.Fields.PageTitle.Values.0') ||
            get(value, 'InternalLink.LinkedComponentValues.0.Title') ||
            get(value, 'SocialNetwork.Values.0') ||
            get(value, 'Promotion.LinkedComponentValues.0.Fields.Link.EmbeddedValues.0.Title.Values.0') ||
            get(value, 'EmbeddedValues.0.Title.Values.0') ||
            get(value, 'Title') ||
            '');
    }
    getUrl(value) {
        return (get(value, 'Component.LinkedComponentValues.0.ResolvedUrl') ||
            get(value, 'Component.LinkedComponentValues.0.Multimedia.Url') ||
            get(value, 'InternalLink.LinkedComponentValues.0.ResolvedUrl') ||
            get(value, 'InternalLinks.LinkedComponentValues.0.ResolvedUrl') ||
            get(value, 'LinkedComponentValues.0.ResolvedUrl') ||
            get(value, 'ResolvedUrl') ||
            get(value, 'External.Values.0') ||
            get(value, 'ExternalLink.Values.0') ||
            get(value, 'Component.LinkedComponentValues.0.Fields.CTA.EmbeddedValues.0.CTA.EmbeddedValues.0.External.Values.0') ||
            get(value, 'Component.LinkedComponentValues.0.Fields.Link.EmbeddedValues.0.External.Values.0') ||
            get(value, 'Link.Values.0') ||
            get(value, 'Promotion.LinkedComponentValues.0.Fields.Link.EmbeddedValues.0.External.Values.0') ||
            get(value, 'Promotion.LinkedComponentValues.0.Fields.Link.EmbeddedValues.0.Component.LinkedComponentValues.0.ResolvedUrl') ||
            get(value, 'EmbeddedValues[0].Component.LinkedComponentValues[0].ResolvedUrl') ||
            get(value, 'EmbeddedValues.0.External.Values.0') ||
            get(value, 'cardDetailUrl') ||
            get(value, 'InternalLink.EmbeddedValues.0.InternalLinks.LinkedComponentValues.0.ResolvedUrl') ||
            get(value, 'InternalLink.EmbeddedValues.0.ExternalLink.Values.0') ||
            '');
    }
    getAnchorHash(value) {
        return get(value, 'AnchorTag.Values.0');
    }
    getPermalinkHash(value) {
        return get(value, 'Permalink.Values.0');
    }
    getFilterListUrl(value) {
        const query = {
            experience: this.getFieldAttractionExperience(value) || '',
            interest: this.getFieldAttractionInterest(value) || '',
            location: this.getFieldAttractionLocation(value) || ''
        };
        return this.getOrSetPageLabels().then(() => {
            return `${get(this.pageLabels, 'lnkFilteredListDetail')}?attraction_experience=${query.experience}&attraction_location=${query.location}&attraction_interest=${query.interest}`;
        });
    }
    getFieldAttractionExperience(field) {
        return (get(field, 'FilterListLink.EmbeddedValues.0.AttractionExperience.KeywordValues.0.Key') ||
            get(field, 'AttractionExpAsset.EmbeddedValues.0.AttractionExperience.KeywordValues.0.Key') ||
            get(field, 'AttractionExperience.KeywordValues.0.Key'));
    }
    getFieldAttractionLocation(field) {
        return (get(field, 'FilterListLink.EmbeddedValues.0.Location.KeywordValues.0.Key') ||
            get(field, 'AttractionLocation.KeywordValues.0.Key') ||
            get(field, 'Location.KeywordValues.0.Key') ||
            get(field, 'AttractionExpAsset.EmbeddedValues.0.AssetDisplayed.LinkedComponentValues.0.MetadataFields.Details.EmbeddedValues.0.AttractionLocation.KeywordValues.0.Key'));
    }
    getFieldAttractionInterest(field) {
        return (get(field, 'FilterListLink.EmbeddedValues.0.InterestSegment.KeywordValues.0.Key') ||
            get(field, 'AttractionInterest.KeywordValues.0.Key') ||
            get(field, 'InterestSegment.KeywordValues.0.Key'));
    }
    getAnalyticsTitle(field) {
        return get(field, 'AnalyticsTitle.Values.0');
    }
}
TcmAnchorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TcmAnchorService, deps: [{ token: i1.PageLabelsResolver }], target: i0.ɵɵFactoryTarget.Injectable });
TcmAnchorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TcmAnchorService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TcmAnchorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.PageLabelsResolver }]; } });

class TcmAnchorComponent {
    constructor(appRegexs, defaultValues, globalDefaults, document, window, tcmAnchorService, route, localeService, appModal, pageLabelsService, miniSitesService, location) {
        this.appRegexs = appRegexs;
        this.defaultValues = defaultValues;
        this.globalDefaults = globalDefaults;
        this.document = document;
        this.window = window;
        this.tcmAnchorService = tcmAnchorService;
        this.route = route;
        this.localeService = localeService;
        this.appModal = appModal;
        this.pageLabelsService = pageLabelsService;
        this.miniSitesService = miniSitesService;
        this.location = location;
        this.fireEvent = new EventEmitter();
        this.routerLinkExact = false;
        this.XPMEnabled = false;
        this.contentSite = !this.globalDefaults.WEB_CONTENT_URL;
        this.multimediaRegexMatched = false;
        this.loaded = false;
        this.modalConfig = {};
        this.anchorTargets = AnchorTarget;
        const locale = this.localeService.getFromStorage();
        this.language = locale.language;
        this.country = locale.country;
        this.route.data.subscribe(data => (this.slug = data.slug));
        this.multimediaRegex = get(this.appRegexs, 'MULTIMEDIA_REGEX', new RegExp('\\.(jpg|JPG|png|PNG|gif|GIF|pdf|PDF|mp4|MP4)'));
        this.pageLabelsService.get().then((pageLabels) => {
            this.pageLabels = pageLabels;
            if (this.ariaLabel && this.target === this.anchorTargets.modal) {
                this.ariaLabel += ' - ' + get(this.pageLabels, 'txtOpenModal', '');
            }
        });
        this.baseHref = this.location.getBaseHref();
    }
    ngOnInit() {
        this.determineTarget(this.embeddedValue);
        this.determineLinkText(this.embeddedValue);
        this.determineUrl(this.embeddedValue);
        this.determineXpath(this.embeddedValue);
        this.determineAnalyticsTitle(this.embeddedValue);
        this.loaded = true;
    }
    ngOnChanges() {
        if (this.loaded) {
            this.ngOnInit();
        }
    }
    determineXpath(value) {
        this.XPath = get(value, 'Title.XPath');
    }
    findComponentTemplateTitle(element) {
        if (!element || get(element, 'localName').includes('app')) {
            return '';
        }
        const title = element.getAttribute('data-component-template-title');
        return title
            ? title
            : this.findComponentTemplateTitle(get(element, 'parentElement'));
    }
    setModalConfig(title, body, status, interceptModal = {}) {
        const componentTemplateTitle = this.anchorRef && this.anchorRef.nativeElement
            ? this.findComponentTemplateTitle(this.anchorRef.nativeElement)
            : '';
        return {
            title,
            componentTemplateTitle,
            body,
            status,
            interceptModal
        };
    }
    onClick(event) {
        if (this.checkInterceptModal()) {
            event.preventDefault();
            this.modalConfig = this.setModalConfig(get(this.embeddedValue, 'NavigationInterceptModal.LinkedComponentValues[0].Fields.title.Values[0]'), get(this.embeddedValue, 'NavigationInterceptModal.LinkedComponentValues[0].Fields.description.Values[0]'), true, {
                url: this.url,
                target: this.target
            });
            return this.appModal.show(this.modalConfig);
        }
        if (this.hash && !this.routerPath) {
            const offset = this.document.querySelector(`#${this.hash}`).offsetTop;
            this.window.scrollTo(0, offset - 60);
        }
        if (this.fireEvent) {
            this.fireEvent.emit(event);
        }
        if (this.target === this.anchorTargets.modal) {
            event.preventDefault();
            const title = get(this.embeddedValue, 'EmbeddedValues[0].Component.LinkedComponentValues[0].Fields.Title.Values[0]') ||
                get(this.embeddedValue, 'Component.LinkedComponentValues[0].Fields.Title.Values[0]') ||
                '';
            const body = get(this.embeddedValue, 'EmbeddedValues[0].Component.LinkedComponentValues[0].Fields.Message.Values[0]') ||
                get(this.embeddedValue, 'Component.LinkedComponentValues[0].Fields.Message.Values[0]') ||
                '';
            if (title || body) {
                this.modalConfig = this.setModalConfig(title, body, true);
                return this.appModal.show(this.modalConfig);
            }
        }
    }
    determineAnalyticsTitle(value) {
        this.analyticsTitle =
            this.tcmAnchorService.getAnalyticsTitle(value) ||
                this.analyticsTitle ||
                '';
    }
    determineTarget(value) {
        if (this.overwriteTarget) {
            this.target = this.overwriteTarget;
            return;
        }
        const target = this.tcmAnchorService.getTarget(value);
        if (this.XPMEnabled) {
            this.target = this.anchorTargets.self;
            return;
        }
        switch (target) {
            case this.anchorTargets.newWindow:
                this.target = get(value, 'Target.KeywordValues.0.Key');
                break;
            case this.anchorTargets.sameWindow:
                this.target = get(value, 'Target.KeywordValues.0.Key');
                break;
            case this.anchorTargets.modal:
                this.target = this.anchorTargets.modal;
                break;
            default:
                this.target = '';
        }
    }
    determineLinkText(value) {
        this.linkText = this.tcmAnchorService.getTitle(value);
        if (this.overwriteLinkText) {
            this.linkText = this.overwriteLinkText;
        }
        this.linkText = (this.linkText || '')
            .replace(/<script.*>.*<\/script>/i, '')
            .trim();
        if (this.linkText.indexOf('>') > -1) {
            this.linkText = (this.linkText || '').replace(' >', '').trim();
            this.chevronClass = 'icon-arrow-right';
        }
        this.linkText = compact(this.linkText.split('&gt;')).join('&gt;');
    }
    determineUrl(value) {
        let link;
        if (this.overwritePath && this.overwriteUrl) {
            console.error('Cannot have both overwritePath and overwriteUrl.');
            return;
        }
        if (this.overwritePath) {
            this.routerPath = this.overwritePath;
            return;
        }
        if (this.overwriteUrl) {
            this.compileUrl(this.overwriteUrl);
            return;
        }
        if (!value) {
            return;
        }
        if (value.FilterListLink ||
            value.AttractionExperience ||
            value.AttractionExpAsset) {
            this.tcmAnchorService.getFilterListUrl(value).then((url) => {
                link = this.compileUrl(url);
            });
            return;
        }
        if (value.AnchorTag || value.Permalink) {
            const hash = value.Permalink
                ? this.tcmAnchorService.getPermalinkHash(value).replace('#', '')
                : this.tcmAnchorService.getAnchorHash(value).replace('#', '');
            link = this.tcmAnchorService.getUrl(value);
            if (!link) {
                link = '';
            }
            this.hash = hash;
            this.compileUrl(link);
            return;
        }
        link = this.tcmAnchorService.getUrl(value);
        this.compileUrl(link);
        if (this.target === this.anchorTargets.modal) {
            if (!this.url && !this.routerPath && !this.chevronClass) {
                this.chevronClass = 'hidden';
            }
            if (this.routerPath && !this.overwriteTarget) {
                this.target = '';
            }
        }
    }
    compileUrl(url) {
        if (!url) {
            return;
        }
        if (this.appRegexs.FORMATTED_LINK_EXP.test(url)) {
            this.url = url;
            this.checkForQuery();
            return;
        }
        else if (this.multimediaRegex.test(url)) {
            this.multimediaRegexMatched = true;
            this.url =
                this.defaultValues.TRIDION_WEBDATA +
                    (url.startsWith('/') ? '' : '/') +
                    url;
            return;
        }
        else if (this.appRegexs.INTERNAL_REGEX.test(url)) {
            url = url.replace(this.appRegexs.INTERNAL_REGEX, '');
            this.routerPath =
                '/' +
                    this.language +
                    `/${this.country}/` +
                    (this.slug ? this.slug.split('/')[0] + '/' : '') +
                    url.replace('/index.html', '').replace('./', '');
            this.checkForQuery();
            this.checkForHash();
            return;
        }
        else if (this.appRegexs.WEB_STORE_EXP.test(url)) {
            if (!this.appRegexs.LANGUAGE_EXP.test(url)) {
                url = url.replace(this.appRegexs.WEB_STORE_EXP, '/' + this.language + '/' + this.country + '/');
            }
            this.url =
                this.defaultValues.WEB_STORE_APP_PATH +
                    url.replace(this.appRegexs.WEB_STORE_EXP, '/');
            this.checkForQuery();
            return;
        }
        else if (this.appRegexs.WEB_PACKAGES_EXP.test(url)) {
            if (!this.appRegexs.LANGUAGE_EXP.test(url)) {
                url = url.replace(this.appRegexs.WEB_PACKAGES_EXP, '/' + this.language + '/' + this.country + '/');
            }
            this.url =
                this.defaultValues.WEB_PACKAGES_APP_PATH +
                    url.replace(this.appRegexs.WEB_PACKAGES_EXP, '/');
            this.checkForQuery();
            return;
        }
        else if (this.appRegexs.CONTENT_URL_EXP.test(url) &&
            this.globalDefaults.WEB_CONTENT_URL) {
            url = url.replace(this.appRegexs.CONTENT_URL_EXP, '/');
            url = this.globalDefaults.WEB_CONTENT_URL + url;
        }
        else if (!this.globalDefaults.WEB_CONTENT_URL &&
            url &&
            !url.includes('%%')) {
            this.routerPath = url
                .replace(this.appRegexs.CONTENT_URL_EXP, '/')
                .replace('/index.html', '');
            this.routerPath = this.miniSitesService.getUrl(this.routerPath);
            if (!url.startsWith('/')) {
                this.url = this.miniSitesService.getUrl(url);
            }
            this.checkForQuery();
            this.checkForHash();
            return;
        }
        if (this.baseHref && url.includes(this.baseHref)) {
            this.routerPath = this.miniSitesService.getUrl(url.replace(this.baseHref, ''));
            this.checkForQuery();
            this.checkForHash();
            return;
        }
        if (!this.globalDefaults.WEB_CONTENT_URL &&
            url &&
            url.length > 1 &&
            url.indexOf('/') === 0) {
            url = url.substring(1);
        }
        url = url.replace('%%DenaliURL%%', this.defaultValues.DENALI_URL);
        url = url.replace('%%HotelsBookingURL%%', this.defaultValues.HOTELS_BOOKING_URL);
        this.url = this.miniSitesService.getUrl(url);
        this.checkForQuery();
    }
    checkForQuery() {
        if (this.routerPath && this.routerPath.indexOf('?') > -1) {
            const routePathSplitted = this.routerPath.split('?');
            this.routerPath = routePathSplitted[0];
            const queryStringParams = routePathSplitted[1];
            this.queryParams = JSON.parse('{"' +
                decodeURI(queryStringParams)
                    .replace(/"/g, '\\"')
                    .replace(/&/g, '","')
                    .replace(/=/g, '":"') +
                '"}');
        }
        if (this.overwriteQueryParams) {
            this.queryParams = this.overwriteQueryParams;
        }
        if (this.url && this.queryParams && !this.routerPath) {
            let queryParams = Object.entries(this.queryParams)
                .map(([key, value]) => `${key}=${value}`)
                .join('&');
            if (this.url.includes('?')) {
                if (this.url.includes('&')) {
                    queryParams += '&';
                }
                this.url = this.url.replace('?', '?' + queryParams);
                return;
            }
            this.url = this.url + '?' + queryParams;
        }
    }
    checkForHash() {
        if (this.routerPath.includes('#')) {
            const routeArray = this.routerPath.split('#');
            this.routerPath = routeArray[0];
            this.hash = routeArray[1];
        }
        if (this.overwriteHash) {
            this.hash = this.overwriteHash;
        }
    }
    closeModal() {
        this.appModal.hide();
    }
    checkInterceptModal() {
        const interceptLocale = get(this.embeddedValue, 'NavigationInterceptModalLocale.KeywordValues[0].Key');
        return `${this.language}/${this.country}` === interceptLocale;
    }
    focus() {
        this.anchorRef.nativeElement.focus();
    }
}
TcmAnchorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TcmAnchorComponent, deps: [{ token: 'AppRegexs' }, { token: 'DefaultValues' }, { token: 'GlobalDefaults' }, { token: DOCUMENT }, { token: WINDOW }, { token: TcmAnchorService }, { token: i2.ActivatedRoute }, { token: i3.LocaleService }, { token: i4.AppModalService }, { token: i1.PageLabelsResolver }, { token: i6.MinisiteService }, { token: i7.LocationStrategy }], target: i0.ɵɵFactoryTarget.Component });
TcmAnchorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: TcmAnchorComponent, selector: "tcm-anchor", inputs: { embeddedValue: "embeddedValue", chevronClass: "chevronClass", useRouterLinkActive: "useRouterLinkActive", routerLinkExact: "routerLinkExact", overwriteLinkText: "overwriteLinkText", overwriteTarget: "overwriteTarget", overwriteQueryParams: "overwriteQueryParams", overwriteHash: "overwriteHash", overwriteRole: "overwriteRole", anchorClass: "anchorClass", anchorStyle: "anchorStyle", analyticsTitle: "analyticsTitle", transclude: "transclude", ariaLabel: "ariaLabel", lang: "lang", tabindex: "tabindex", overwriteUrl: "overwriteUrl", overwritePath: "overwritePath" }, outputs: { fireEvent: "fireEvent" }, viewQueries: [{ propertyName: "anchorRef", first: true, predicate: ["anchorRef"], descendants: true, read: ElementRef }, { propertyName: "routeActive", first: true, predicate: ["routeActive"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<ng-template #contentTemplate>\r\n\t<span>\r\n\t\t<ng-content></ng-content>\r\n\t</span>\r\n</ng-template>\r\n\r\n<ng-template #linkTextTemplate>\r\n\t<ng-container *ngIf=\"linkText\">\r\n\t\t<span [innerHtml]=\"linkText\" [attr.aria-label]=\"ariaLabel\"></span>\r\n\t\t<i\r\n\t\t\t*ngIf=\"!routeActive?.isActive || !useRouterLinkActive\"\r\n\t\t\tclass=\"icon {{\r\n\t\t\t\tchevronClass || (contentSite ? 'icon-arrow-right' : 'hidden')\r\n\t\t\t}}\"\r\n\t\t\taria-hidden=\"true\"\r\n\t\t>\r\n\t\t</i>\r\n\t</ng-container>\r\n</ng-template>\r\n\r\n<ng-template #linkRouteTemplate>\r\n\t<ng-container *ngIf=\"routerPath; then route; else noRoute\"></ng-container>\r\n\r\n\t<ng-template #route>\r\n\t\t<a\r\n\t\t\t[attr.role]=\"overwriteRole\"\r\n\t\t\t[attr.class]=\"anchorClass\"\r\n\t\t\t[target]=\"target ? target : '_self'\"\r\n\t\t\t[attr.analytics-title]=\"analyticsTitle || linkText || undefined\"\r\n\t\t\t[ngStyle]=\"anchorStyle\"\r\n\t\t\t(click)=\"onClick($event)\"\r\n\t\t\t[routerLink]=\"routerPath\"\r\n\t\t\t[fragment]=\"hash || undefined\"\r\n\t\t\t[queryParams]=\"queryParams\"\r\n\t\t\t[routerLinkActive]=\"[useRouterLinkActive ? 'current' : '']\"\r\n\t\t\t[routerLinkActiveOptions]=\"{ exact: routerLinkExact }\"\r\n\t\t\t[attr.aria-label]=\"ariaLabel\"\r\n\t\t\t[attr.lang]=\"lang\"\r\n\t\t\t[attr.tabindex]=\"target === anchorTargets?.modal ? '0' : tabindex\"\r\n\t\t\t#routeActive=\"routerLinkActive\"\r\n\t\t\tdraggable=\"false\"\r\n\t\t\t#anchorRef\r\n\t\t>\r\n\t\t\t<ng-container\r\n\t\t\t\t*ngIf=\"!transclude; then linkTextTemplate; else contentTemplate\"\r\n\t\t\t></ng-container>\r\n\t\t</a>\r\n\t</ng-template>\r\n\r\n\t<ng-template #noRoute>\r\n\t\t<a\r\n\t\t\t[attr.class]=\"anchorClass\"\r\n\t\t\t[attr.analytics-title]=\"analyticsTitle || linkText || undefined\"\r\n\t\t\t[attr.role]=\"\r\n\t\t\t\toverwriteRole || (target === anchorTargets.modal ? 'button' : undefined)\r\n\t\t\t\"\r\n\t\t\t[attr.target]=\"url ? target : undefined\"\r\n\t\t\t[attr.href]=\"url ? url : undefined\"\r\n\t\t\t[attr.tabindex]=\"target === anchorTargets?.modal ? '0' : tabindex\"\r\n\t\t\t[attr.aria-label]=\"ariaLabel\"\r\n\t\t\t[attr.lang]=\"lang\"\r\n\t\t\tdraggable=\"false\"\r\n\t\t\t[ngStyle]=\"anchorStyle\"\r\n\t\t\t(click)=\"onClick($event)\"\r\n\t\t\t(keydown.enter)=\"\r\n\t\t\t\ttarget === anchorTargets.modal ? onClick($event) : undefined\r\n\t\t\t\"\r\n\t\t\t(keydown.space)=\"\r\n\t\t\t\ttarget === anchorTargets.modal ? onClick($event) : undefined\r\n\t\t\t\"\r\n\t\t\t#anchorRef\r\n\t\t>\r\n\t\t\t<ng-container\r\n\t\t\t\t*ngIf=\"!transclude; then linkTextTemplate; else contentTemplate\"\r\n\t\t\t></ng-container>\r\n\t\t\t<span\r\n\t\t\t\t*ngIf=\"!ariaLabel && target === anchorTargets.modal && pageLabels\"\r\n\t\t\t\t[innerHTML]=\"pageLabels.txtOpenModal\"\r\n\t\t\t\tclass=\"sr-only\"\r\n\t\t\t>\r\n\t\t\t</span>\r\n\t\t</a>\r\n\t</ng-template>\r\n</ng-template>\r\n\r\n<ng-container\r\n\t*ngIf=\"\r\n\t\tlinkText || url || routerPath;\r\n\t\tthen linkRouteTemplate;\r\n\t\telse contentTemplate\r\n\t\"\r\n></ng-container>\r\n", styles: [".outline-style-primary-blue,:host-context(.alt-nav) a:focus,:host-context(.ultra) a:focus,:host-context(.nav-links.secondary) a:focus,:host-context(.secondary) a:focus,:host-context(.menu-item) a:focus,:host-context(.sub-link) a:focus{outline:1px dashed #0368d9;outline-offset:2px}.outline-style-primary-white,.cart-icon-link:focus,.header-logo:focus,:host-context(.utility-bar) a:focus,:host-context(.dropdown-nav) a:focus,:host-context(.alt-nav-header-links) a:focus,:host-context(.mobile-nav-highlighted) a:focus,:host-context(.white-no-hover) a:focus{outline:1px dashed #fff;outline-offset:2px}.font-12{font-size:12px!important}.font-14{font-size:14px!important}.font-16{font-size:16px!important}.font-18{font-size:18px!important}.font-20{font-size:20px!important}.font-22{font-size:22px!important}.font-24{font-size:24px!important}.font-26{font-size:26px!important}.font-28{font-size:28px!important}.font-30{font-size:30px!important}.font-32{font-size:32px!important}.fade-me-in{animation:fadeIn .25s;-webkit-animation:fadeIn .25s;-moz-animation:fadeIn .25s;-o-animation:fadeIn .25s}@keyframes fadeIn{0%{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}@keyframes fadeIn{0%{opacity:0;-webkit-transform:scale(.95)}to{opacity:1;-webkit-transform:scale(1)}}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}:host-context(.size-xsmall) a{font-size:12px}:host-context(.size-small) a{font-size:14px}a:after,i:after{font-weight:400}:host-context(.sub-link) a{margin:16px 0;display:flex}:host-context(.menu-item) a{font-size:18px;font-weight:500;display:flex;height:62px;flex-direction:column;justify-content:center;margin-bottom:4px;margin-top:4px}:host-context(.secondary) a{font-weight:400}:host-context(.white-no-hover) a{color:#fff}:host-context(.white-no-hover) a:hover{color:#fff}:host-context(.alt-nav-header-links) a,:host-context(.mobile-nav-highlighted) a{color:#fff;padding:10px 16px;display:block;width:100%}:host-context(.mobile-nav-primary) a,:host-context(.mobile-nav-secondary) a,:host-context(.mobile-nav-tertiary) a,:host-context(.page-content) a,:host-context(.promo-link) a{display:inline}:host-context(.mobile-nav-primary) a.btn,:host-context(.mobile-nav-secondary) a.btn,:host-context(.mobile-nav-tertiary) a.btn,:host-context(.page-content) a.btn,:host-context(.promo-link) a.btn{display:inline-block}:host-context(.mobile-nav-primary) i,:host-context(.mobile-nav-secondary) i,:host-context(.mobile-nav-tertiary) i,:host-context(.page-content) i,:host-context(.promo-link) i{color:#036ce2;padding-left:4px;position:relative;top:2px}:host-context(.dropdown-nav) a{color:#fff;font-size:1em;line-height:1.5em;transition:all .2s linear}:host-context(.dropdown-nav) a i{position:relative;visibility:hidden;top:2px}:host-context(.dropdown-nav) a:hover{font-size:1.125em}:host-context(.dropdown-nav) a:hover i{visibility:visible}:host-context(.profile-header) a{display:flex;align-items:center}:host-context(.profile-header) i{top:0}:host-context(.footer-links) a{color:#036ce2}:host-context(.footer-links) i{display:none}:host-context(.copyright-footer) a,:host-context(.contact-links) a{display:flex;align-items:center}:host-context(.copyright-footer) a i,:host-context(.contact-links) a i{margin-left:2px}:host-context(.carousel-content) a{color:#fff}:host-context(.carousel-content) a i{color:#fff}:host-context(.trip-tip) a{color:#fff;display:block;font-size:14px}:host-context(.trip-tip) a i{color:#fff}@media print,screen and (min-width: 48em){:host-context(.trip-tip) a{font-size:24px}}:host-context(.nav-links.secondary) a{font-size:.85em;font-weight:400}:host-context(.ultra) a{font-weight:900}:host-context(.alt-nav) a{align-items:center;display:flex;text-transform:uppercase;border:2px solid #fff;border-radius:5px;background-color:#036ce2;padding:10px 16px;font-size:14px;line-height:20px}:host-context(.alt-nav) a i{padding-left:4px}:host-context(.utility-bar) a{background-color:#fff;color:#036ce2;width:100%;min-height:40px;border-radius:12px;margin-right:16px;text-align:center;line-height:1.2;font-size:.75em;font-weight:600;display:flex;flex:1;justify-content:center;align-items:center;padding:4px 8px}.current{color:#162b75;pointer-events:none}.current span{display:inline-block}.white-background .alt-nav :host-context{background-color:#0368d9}.white-background .footer-links :host-context a{color:#0368d9}.white-background .mobile-nav-primary :host-context i,.white-background .mobile-nav-secondary :host-context i,.white-background .mobile-nav-tertiary :host-context i,.white-background .page-content :host-context i,.white-background .promo-link :host-context i{color:#0368d9}.header-logo{display:flex;height:100%}.cart-icon-link{position:relative;display:block}:host-context(.nav-header-links) a{display:flex;align-items:center}\n"], directives: [{ type: i7.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i2.RouterLinkWithHref, selector: "a[routerLink],area[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "preserveFragment", "skipLocationChange", "replaceUrl", "state", "relativeTo", "routerLink"] }, { type: i7.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { type: i2.RouterLinkActive, selector: "[routerLinkActive]", inputs: ["routerLinkActiveOptions", "routerLinkActive"], outputs: ["isActiveChange"], exportAs: ["routerLinkActive"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TcmAnchorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tcm-anchor', template: "<ng-template #contentTemplate>\r\n\t<span>\r\n\t\t<ng-content></ng-content>\r\n\t</span>\r\n</ng-template>\r\n\r\n<ng-template #linkTextTemplate>\r\n\t<ng-container *ngIf=\"linkText\">\r\n\t\t<span [innerHtml]=\"linkText\" [attr.aria-label]=\"ariaLabel\"></span>\r\n\t\t<i\r\n\t\t\t*ngIf=\"!routeActive?.isActive || !useRouterLinkActive\"\r\n\t\t\tclass=\"icon {{\r\n\t\t\t\tchevronClass || (contentSite ? 'icon-arrow-right' : 'hidden')\r\n\t\t\t}}\"\r\n\t\t\taria-hidden=\"true\"\r\n\t\t>\r\n\t\t</i>\r\n\t</ng-container>\r\n</ng-template>\r\n\r\n<ng-template #linkRouteTemplate>\r\n\t<ng-container *ngIf=\"routerPath; then route; else noRoute\"></ng-container>\r\n\r\n\t<ng-template #route>\r\n\t\t<a\r\n\t\t\t[attr.role]=\"overwriteRole\"\r\n\t\t\t[attr.class]=\"anchorClass\"\r\n\t\t\t[target]=\"target ? target : '_self'\"\r\n\t\t\t[attr.analytics-title]=\"analyticsTitle || linkText || undefined\"\r\n\t\t\t[ngStyle]=\"anchorStyle\"\r\n\t\t\t(click)=\"onClick($event)\"\r\n\t\t\t[routerLink]=\"routerPath\"\r\n\t\t\t[fragment]=\"hash || undefined\"\r\n\t\t\t[queryParams]=\"queryParams\"\r\n\t\t\t[routerLinkActive]=\"[useRouterLinkActive ? 'current' : '']\"\r\n\t\t\t[routerLinkActiveOptions]=\"{ exact: routerLinkExact }\"\r\n\t\t\t[attr.aria-label]=\"ariaLabel\"\r\n\t\t\t[attr.lang]=\"lang\"\r\n\t\t\t[attr.tabindex]=\"target === anchorTargets?.modal ? '0' : tabindex\"\r\n\t\t\t#routeActive=\"routerLinkActive\"\r\n\t\t\tdraggable=\"false\"\r\n\t\t\t#anchorRef\r\n\t\t>\r\n\t\t\t<ng-container\r\n\t\t\t\t*ngIf=\"!transclude; then linkTextTemplate; else contentTemplate\"\r\n\t\t\t></ng-container>\r\n\t\t</a>\r\n\t</ng-template>\r\n\r\n\t<ng-template #noRoute>\r\n\t\t<a\r\n\t\t\t[attr.class]=\"anchorClass\"\r\n\t\t\t[attr.analytics-title]=\"analyticsTitle || linkText || undefined\"\r\n\t\t\t[attr.role]=\"\r\n\t\t\t\toverwriteRole || (target === anchorTargets.modal ? 'button' : undefined)\r\n\t\t\t\"\r\n\t\t\t[attr.target]=\"url ? target : undefined\"\r\n\t\t\t[attr.href]=\"url ? url : undefined\"\r\n\t\t\t[attr.tabindex]=\"target === anchorTargets?.modal ? '0' : tabindex\"\r\n\t\t\t[attr.aria-label]=\"ariaLabel\"\r\n\t\t\t[attr.lang]=\"lang\"\r\n\t\t\tdraggable=\"false\"\r\n\t\t\t[ngStyle]=\"anchorStyle\"\r\n\t\t\t(click)=\"onClick($event)\"\r\n\t\t\t(keydown.enter)=\"\r\n\t\t\t\ttarget === anchorTargets.modal ? onClick($event) : undefined\r\n\t\t\t\"\r\n\t\t\t(keydown.space)=\"\r\n\t\t\t\ttarget === anchorTargets.modal ? onClick($event) : undefined\r\n\t\t\t\"\r\n\t\t\t#anchorRef\r\n\t\t>\r\n\t\t\t<ng-container\r\n\t\t\t\t*ngIf=\"!transclude; then linkTextTemplate; else contentTemplate\"\r\n\t\t\t></ng-container>\r\n\t\t\t<span\r\n\t\t\t\t*ngIf=\"!ariaLabel && target === anchorTargets.modal && pageLabels\"\r\n\t\t\t\t[innerHTML]=\"pageLabels.txtOpenModal\"\r\n\t\t\t\tclass=\"sr-only\"\r\n\t\t\t>\r\n\t\t\t</span>\r\n\t\t</a>\r\n\t</ng-template>\r\n</ng-template>\r\n\r\n<ng-container\r\n\t*ngIf=\"\r\n\t\tlinkText || url || routerPath;\r\n\t\tthen linkRouteTemplate;\r\n\t\telse contentTemplate\r\n\t\"\r\n></ng-container>\r\n", styles: [".outline-style-primary-blue,:host-context(.alt-nav) a:focus,:host-context(.ultra) a:focus,:host-context(.nav-links.secondary) a:focus,:host-context(.secondary) a:focus,:host-context(.menu-item) a:focus,:host-context(.sub-link) a:focus{outline:1px dashed #0368d9;outline-offset:2px}.outline-style-primary-white,.cart-icon-link:focus,.header-logo:focus,:host-context(.utility-bar) a:focus,:host-context(.dropdown-nav) a:focus,:host-context(.alt-nav-header-links) a:focus,:host-context(.mobile-nav-highlighted) a:focus,:host-context(.white-no-hover) a:focus{outline:1px dashed #fff;outline-offset:2px}.font-12{font-size:12px!important}.font-14{font-size:14px!important}.font-16{font-size:16px!important}.font-18{font-size:18px!important}.font-20{font-size:20px!important}.font-22{font-size:22px!important}.font-24{font-size:24px!important}.font-26{font-size:26px!important}.font-28{font-size:28px!important}.font-30{font-size:30px!important}.font-32{font-size:32px!important}.fade-me-in{animation:fadeIn .25s;-webkit-animation:fadeIn .25s;-moz-animation:fadeIn .25s;-o-animation:fadeIn .25s}@keyframes fadeIn{0%{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}@keyframes fadeIn{0%{opacity:0;-webkit-transform:scale(.95)}to{opacity:1;-webkit-transform:scale(1)}}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}:host-context(.size-xsmall) a{font-size:12px}:host-context(.size-small) a{font-size:14px}a:after,i:after{font-weight:400}:host-context(.sub-link) a{margin:16px 0;display:flex}:host-context(.menu-item) a{font-size:18px;font-weight:500;display:flex;height:62px;flex-direction:column;justify-content:center;margin-bottom:4px;margin-top:4px}:host-context(.secondary) a{font-weight:400}:host-context(.white-no-hover) a{color:#fff}:host-context(.white-no-hover) a:hover{color:#fff}:host-context(.alt-nav-header-links) a,:host-context(.mobile-nav-highlighted) a{color:#fff;padding:10px 16px;display:block;width:100%}:host-context(.mobile-nav-primary) a,:host-context(.mobile-nav-secondary) a,:host-context(.mobile-nav-tertiary) a,:host-context(.page-content) a,:host-context(.promo-link) a{display:inline}:host-context(.mobile-nav-primary) a.btn,:host-context(.mobile-nav-secondary) a.btn,:host-context(.mobile-nav-tertiary) a.btn,:host-context(.page-content) a.btn,:host-context(.promo-link) a.btn{display:inline-block}:host-context(.mobile-nav-primary) i,:host-context(.mobile-nav-secondary) i,:host-context(.mobile-nav-tertiary) i,:host-context(.page-content) i,:host-context(.promo-link) i{color:#036ce2;padding-left:4px;position:relative;top:2px}:host-context(.dropdown-nav) a{color:#fff;font-size:1em;line-height:1.5em;transition:all .2s linear}:host-context(.dropdown-nav) a i{position:relative;visibility:hidden;top:2px}:host-context(.dropdown-nav) a:hover{font-size:1.125em}:host-context(.dropdown-nav) a:hover i{visibility:visible}:host-context(.profile-header) a{display:flex;align-items:center}:host-context(.profile-header) i{top:0}:host-context(.footer-links) a{color:#036ce2}:host-context(.footer-links) i{display:none}:host-context(.copyright-footer) a,:host-context(.contact-links) a{display:flex;align-items:center}:host-context(.copyright-footer) a i,:host-context(.contact-links) a i{margin-left:2px}:host-context(.carousel-content) a{color:#fff}:host-context(.carousel-content) a i{color:#fff}:host-context(.trip-tip) a{color:#fff;display:block;font-size:14px}:host-context(.trip-tip) a i{color:#fff}@media print,screen and (min-width: 48em){:host-context(.trip-tip) a{font-size:24px}}:host-context(.nav-links.secondary) a{font-size:.85em;font-weight:400}:host-context(.ultra) a{font-weight:900}:host-context(.alt-nav) a{align-items:center;display:flex;text-transform:uppercase;border:2px solid #fff;border-radius:5px;background-color:#036ce2;padding:10px 16px;font-size:14px;line-height:20px}:host-context(.alt-nav) a i{padding-left:4px}:host-context(.utility-bar) a{background-color:#fff;color:#036ce2;width:100%;min-height:40px;border-radius:12px;margin-right:16px;text-align:center;line-height:1.2;font-size:.75em;font-weight:600;display:flex;flex:1;justify-content:center;align-items:center;padding:4px 8px}.current{color:#162b75;pointer-events:none}.current span{display:inline-block}.white-background .alt-nav :host-context{background-color:#0368d9}.white-background .footer-links :host-context a{color:#0368d9}.white-background .mobile-nav-primary :host-context i,.white-background .mobile-nav-secondary :host-context i,.white-background .mobile-nav-tertiary :host-context i,.white-background .page-content :host-context i,.white-background .promo-link :host-context i{color:#0368d9}.header-logo{display:flex;height:100%}.cart-icon-link{position:relative;display:block}:host-context(.nav-header-links) a{display:flex;align-items:center}\n"] }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['AppRegexs']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['DefaultValues']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['GlobalDefaults']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: TcmAnchorService }, { type: i2.ActivatedRoute }, { type: i3.LocaleService }, { type: i4.AppModalService }, { type: i1.PageLabelsResolver }, { type: i6.MinisiteService }, { type: i7.LocationStrategy }]; }, propDecorators: { anchorRef: [{
                type: ViewChild,
                args: ['anchorRef', { read: ElementRef }]
            }], routeActive: [{
                type: ViewChild,
                args: ['routeActive']
            }], fireEvent: [{
                type: Output
            }], embeddedValue: [{
                type: Input
            }], chevronClass: [{
                type: Input
            }], useRouterLinkActive: [{
                type: Input
            }], routerLinkExact: [{
                type: Input
            }], overwriteLinkText: [{
                type: Input
            }], overwriteTarget: [{
                type: Input
            }], overwriteQueryParams: [{
                type: Input
            }], overwriteHash: [{
                type: Input
            }], overwriteRole: [{
                type: Input
            }], anchorClass: [{
                type: Input
            }], anchorStyle: [{
                type: Input
            }], analyticsTitle: [{
                type: Input
            }], transclude: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], lang: [{
                type: Input
            }], tabindex: [{
                type: Input
            }], overwriteUrl: [{
                type: Input
            }], overwritePath: [{
                type: Input
            }] } });

class TcmAnchorModule {
}
TcmAnchorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TcmAnchorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TcmAnchorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TcmAnchorModule, declarations: [TcmAnchorComponent], imports: [CommonModule, RouterModule, LocaleModule, PageLabelsModule], exports: [TcmAnchorComponent] });
TcmAnchorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TcmAnchorModule, imports: [[CommonModule, RouterModule, LocaleModule, PageLabelsModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: TcmAnchorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, RouterModule, LocaleModule, PageLabelsModule],
                    exports: [TcmAnchorComponent],
                    declarations: [TcmAnchorComponent]
                }]
        }] });

export { TcmAnchorComponent, TcmAnchorModule, TcmAnchorService };
